import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Switch,
  Select,
  MenuItem,
  Button,
  Divider,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const API_URL = process.env.REACT_APP_API_URL;

export default function CompaniesSection({ user, data, onUpdated }: any) {
  const { t } = useAppTranslation("common");
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [selectedToAdd, setSelectedToAdd] = useState<any | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const assigned = data?.companies || [];

  useEffect(() => {
    const loadCompanies = async () => {
      const res = await apiFetch(`${API_URL}/companies`);
      const companies = await res.json();
      setAllCompanies(Array.isArray(companies) ? companies : []);
    };

    loadCompanies();
  }, []);

  const unassigned = allCompanies.filter(
    (c: any) => !assigned.some((a: any) => a.company_id === c.id)
  );

  const selectedCompany = assigned.find(
    (c: any) => c.company_id === selectedCompanyId
  );

  const handleAssign = async () => {
    if (!selectedToAdd) return;

    await apiFetch(
      `${API_URL}/users/${user.id}/companies/${selectedToAdd.id}`,
      { method: "POST" }
    );

    setSelectedCompanyId(selectedToAdd.id);
    setSelectedToAdd(null);
    onUpdated();
  };

  const handleUpdateAssignable = async (checked: boolean) => {
    if (!selectedCompany) return;

    await apiFetch(
      `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}/assignable`,
      {
        method: "PATCH",
        body: JSON.stringify({
          can_be_assigned_documents: checked,
        }),
        headers: { "Content-Type": "application/json" },
      }
    );

    onUpdated();
  };

  const handleUpdateApproveWithoutMpk = async (checked: boolean) => {
    if (!selectedCompany) return;

    await apiFetch(
      `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}/approve-without-mpk`,
      {
        method: "PATCH",
        body: JSON.stringify({
          can_approve_without_mpk: checked,
        }),
        headers: { "Content-Type": "application/json" },
      }
    );

    onUpdated();
  };

  const handleUpdateMarkProcessedExternally = async (checked: boolean) => {
    if (!selectedCompany) return;

    await apiFetch(
      `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}/mark-processed-externally`,
      {
        method: "PATCH",
        body: JSON.stringify({
          can_mark_processed_externally: checked,
        }),
        headers: { "Content-Type": "application/json" },
      }
    );

    onUpdated();
  };

  const handleUpdateMarkProcessed = async (checked: boolean) => {
    if (!selectedCompany) return;

    await apiFetch(
      `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}/mark-processed`,
      {
        method: "PATCH",
        body: JSON.stringify({
          can_mark_processed: checked,
        }),
        headers: { "Content-Type": "application/json" },
      }
    );

    onUpdated();
  };

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: 2,
        p: 2,
        border: "1px solid #e5e7eb",
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ color: "#1f2937" }}
      >
        {t("Firmy")}
      </Typography>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {/* ADD */}
        <Stack direction="row" spacing={1}>
          <Autocomplete
            fullWidth
            size="small"
            options={unassigned}
            getOptionLabel={(o: any) => o.name}
            value={selectedToAdd}
            onChange={(_, v) => setSelectedToAdd(v)}
            renderInput={(params) => (
              <TextField {...params} label={t("Dodaj firmę")} />
            )}
          />
          <Button
            variant="contained"
            disabled={!selectedToAdd}
            onClick={handleAssign}
          >
            {t("Dodaj")}
          </Button>
        </Stack>

        {/* SELECT EXISTING */}
        <Autocomplete
          fullWidth
          size="small"
          options={assigned}
          getOptionLabel={(o: any) => o.company_name}
          value={selectedCompany || null}
          onChange={(_, v) => setSelectedCompanyId(v?.company_id || null)}
          renderInput={(params) => (
            <TextField {...params} label={t("Wybierz firmę")} />
          )}
        />

        {/* DETAILS */}
        {selectedCompany && (
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: "#e5e7eb",
              backgroundColor: "#fafafa",
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Typography fontWeight={600}>
                  {selectedCompany.company_name}
                </Typography>

                <Divider />

                {/* VISIBILITY */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontWeight: 500 }}
                  >
                    {t("Widoczność dokumentów")}
                  </Typography>

                  <Select
                    fullWidth
                    size="small"
                    value={selectedCompany.document_visibility}
                    onChange={(e) =>
                      apiFetch(
                        `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}/visibility`,
                        {
                          method: "PATCH",
                          body: JSON.stringify({
                            document_visibility: e.target.value,
                          }),
                          headers: { "Content-Type": "application/json" },
                        }
                      ).then(onUpdated)
                    }
                    sx={{ mt: 0.5 }}
                  >
                    <MenuItem value="all">{t("Wszystkie dokumenty")}</MenuItem>
                    <MenuItem value="assigned_only">
                      {t("Tylko przypisane")}
                    </MenuItem>
                  </Select>
                </Box>

                {/* PERMISSIONS */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontWeight: 500 }}
                  >
                    {t("Uprawnienia")}
                  </Typography>

                  <Stack spacing={1} sx={{ mt: 1 }}>
                                        <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        {t("Może zarządzać kontami bankowymi")}
                      </Typography>
                      <Switch
                        checked={selectedCompany.can_manage_bank_accounts}
                        onChange={(e) =>
                          apiFetch(
                            `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}/bank-accounts-permission`,
                            {
                              method: "PATCH",
                              body: JSON.stringify({
                                can_manage_bank_accounts:
                                  e.target.checked,
                              }),
                              headers: { "Content-Type": "application/json" },
                            }
                          ).then(onUpdated)
                        }
                      />
                    </Stack>
                    
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        {t("Może przypisywać dokumenty")}
                      </Typography>
                      <Switch
                        checked={selectedCompany.is_company_admin}
                        onChange={(e) =>
                          apiFetch(
                            `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}/company-admin`,
                            {
                              method: "PATCH",
                              body: JSON.stringify({
                                is_company_admin: e.target.checked,
                              }),
                              headers: { "Content-Type": "application/json" },
                            }
                          ).then(onUpdated)
                        }
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Widoczny na liście przypisywania dokumentów")}
                        </Typography>

                        <Typography variant="caption" sx={{ color: "#6b7280" }}>
                          {t("Wyłącz dla osób, którym nie powinno się przypisywać dokumentów")}
                        </Typography>
                      </Box>

                      <Switch
                        checked={selectedCompany.can_be_assigned_documents ?? true}
                        onChange={(e) => handleUpdateAssignable(e.target.checked)}
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Może akceptować dokumenty bez uzupełnienie wymiarów")}
                        </Typography>

                        <Typography variant="caption" sx={{ color: "#6b7280" }}>
                          {t("Pozwala zaakceptować dokument mimo pustej lub nieuzupełnionej tabeli MPK")}
                        </Typography>
                      </Box>

                      <Switch
                        checked={selectedCompany.can_approve_without_mpk ?? false}
                        onChange={(e) => handleUpdateApproveWithoutMpk(e.target.checked)}
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Może oznaczać dokumenty przetworzone zewnętrznie")}
                        </Typography>

                        <Typography variant="caption" sx={{ color: "#6b7280" }}>
                          {t("Pokazuje kolumnę Przetworzono zewnętrznie w tabeli dokumentów")}
                        </Typography>
                      </Box>

                      <Switch
                        checked={selectedCompany.can_mark_processed_externally ?? false}
                        onChange={(e) =>
                          handleUpdateMarkProcessedExternally(e.target.checked)
                        }
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Może oznaczać jako przetworzone")}
                        </Typography>

                        <Typography variant="caption" sx={{ color: "#6b7280" }}>
                          {t("Pokazuje kolumnę Przetworzono w tabeli dokumentów")}
                        </Typography>
                      </Box>

                      <Switch
                        checked={selectedCompany.can_mark_processed ?? false}
                        onChange={(e) => handleUpdateMarkProcessed(e.target.checked)}
                      />
                    </Stack>   
                  </Stack>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    color="error"
                    size="small"
                    onClick={() =>
                      apiFetch(
                        `${API_URL}/users/${user.id}/companies/${selectedCompany.company_id}`,
                        { method: "DELETE" }
                      ).then(onUpdated)
                    }
                  >
                    {t("Usuń dostęp")}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
}